import { stateDistricts } from '../../../data/stateDistricts';
import {
  CaseParty,
  CreditorsStateType,
  DirectMailingType,
  formDataTypes,
  mapCaliforniaRules,
  NoticeType,
  PDFFile,
  USER_SUPPLIED,
} from '../../../utils/constant';
import { DIRECT_MAILING_PERSONALIZED_ENABLED } from '../../../utils/feature-flags';

export const TABS: Record<NoticeType['type'], { label: string; componentName: string }[]> = {
  'notice-bk': [
    { label: 'Add Case Information', componentName: 'CourtCasePage' },
    { label: 'Add Documents', componentName: 'DocumentsPage' },
    { label: 'Add Addresses', componentName: 'AddressPage' },
    { label: 'Set Preferences', componentName: 'PreferencesPage' },
    { label: 'Review & Submit', componentName: 'ReviewAndSubmitPage' },
  ],
  legal: [
    { label: 'Add Case Information', componentName: 'CourtCasePage' },
    { label: 'Add Documents', componentName: 'DocumentsPage' },
    { label: 'Add Addresses', componentName: 'AddressPage' },
    { label: 'Set Preferences', componentName: 'PreferencesPage' },
    { label: 'Review & Submit', componentName: 'ReviewAndSubmitPage' },
  ],
  // When DIRECT_MAILING_PERSONALIZED_ENABLED is false the Select Mailing Type
  // step is hidden and users land directly on Add Documents (Non-Personalized).
  // When the flag is re-enabled the full 5-step flow is restored.
  direct: DIRECT_MAILING_PERSONALIZED_ENABLED
    ? [
        { label: 'Select Mailing Type', componentName: 'CourtCasePage' },
        { label: 'Add Documents', componentName: 'DocumentsPage' },
        { label: 'Add Addresses', componentName: 'AddressPage' },
        { label: 'Set Preferences', componentName: 'PreferencesPage' },
        { label: 'Review & Submit', componentName: 'ReviewAndSubmitPage' },
      ]
    : [
        { label: 'Add Documents', componentName: 'DocumentsPage' },
        { label: 'Add Addresses', componentName: 'AddressPage' },
        { label: 'Set Preferences', componentName: 'PreferencesPage' },
        { label: 'Review & Submit', componentName: 'ReviewAndSubmitPage' },
      ],
  341: [
    // { label: 'Add Case Information' },
    // { label: 'Notice settings' },
    // { label: 'Select Addresses' },
    // { label: 'Set preferences' },
    // { label: 'Review & Submit' },
  ],
};

export const getStepUrlPaths = (directMailingType: DirectMailingType | null, noticeType?: string) => {
  // Legal and Bankruptcy always use the full 5-step path and are never affected
  // by the Direct Mailing feature flag.
  if (noticeType === 'legal' || noticeType === 'notice-bk') {
    return ['add-case-information', 'add-documents', 'add-addresses', 'set-preferences', 'review-submit'];
  }

  if (!DIRECT_MAILING_PERSONALIZED_ENABLED) {
    // Flag off: Non-Personalized is the only option — skip the Select Mailing Type
    // step (add-case-information) entirely. Add Documents is always the first URL.
    return ['add-documents', 'add-addresses', 'set-preferences', 'review-submit'];
  }
  // Flag on: original 5-step (non-personalized) or 4-step (personalized) flow.
  return directMailingType?.type === 'direct_mail_personalized'
    ? ['add-case-information', 'add-documents', 'set-preferences', 'review-submit']
    : ['add-case-information', 'add-documents', 'add-addresses', 'set-preferences', 'review-submit'];
};

export const getTabs = (options: { condition: string; noticeType: NoticeType }) => {};

export const SUMMARY_COMPONENTS = {
  'notice-bk': [
    'CaseInformationSummary',
    'DocumentsSummary',
    'RecipientsSummary',
    'PreferencesSummary',
    'PaymentMethodSummary',
  ],
  341: ['CaseInformationSummary', 'PreferencesSummary', 'RecipientsSummary', 'PaymentMethodSummary'],
  legal: [
    'CaseInformationSummary',
    'DocumentsSummary',
    'RecipientsSummary',
    'PreferencesSummary',
    'PaymentMethodSummary',
  ],
  direct: ['DocumentsSummary', 'RecipientsSummary', 'PreferencesSummary', 'PaymentMethodSummary'],
};

export const getJourneyButtons = (
  noticeType: NoticeType,
  formData: formDataTypes,
  shouldJumpToSummary: boolean,
): Partial<{ [key: number]: string }> => {
  switch (noticeType.type) {
    case 'legal':
    case 'notice-bk':
      return {
        0: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        1: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        2: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        3: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        4: 'Submit',
      };

    case 'direct':
      if (!DIRECT_MAILING_PERSONALIZED_ENABLED) {
        // Flag off: 4-step Non-Personalized flow (indices 0-3)
        return {
          0: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
          1: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
          2: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
          3: 'Submit',
        };
      }
      if (formData.directMailingType?.type === 'direct_mail_personalized') {
        return {
          0: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
          1: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
          2: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
          3: 'Submit',
        };
      }
      return {
        0: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        1: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        2: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        3: shouldJumpToSummary ? 'Review & Submit' : 'Continue',
        4: 'Submit',
      };

    default:
      // Handle unexpected types if necessary
      return {};
  }
};

export const getBankruptcyForm = (
  bankruptcyType: string,
  caseChapter: string,
  proofClaim?: string,
  subChapterV?: string,
): string => {
  switch (caseChapter) {
    case 'Chapter 7':
      if (bankruptcyType === 'Individual or Joint Debtors') {
        return proofClaim === 'Yes' ? '309B' : '309A';
      } else if (bankruptcyType === 'Corporation or Partnership') {
        return proofClaim === 'Yes' ? '309D' : '309C';
      }
      break;
    case 'Chapter 11':
      if (bankruptcyType === 'Individual or Joint Debtors') {
        return subChapterV === 'Yes' ? '309E2' : '309E1';
      } else if (bankruptcyType === 'Corporation or Partnership') {
        return subChapterV === 'Yes' ? '309F2' : '309F1';
      }
      break;
    case 'Chapter 12':
      return bankruptcyType === 'Individual or Joint Debtors' ? '309G' : '309H';
    case 'Chapter 13':
      return '309I';
    default:
      return '';
  }

  throw new Error('Invalid parameters provided.');
};

const generate341Data = (bk341FormType: string, noticeState: formDataTypes) => {
  let threeFourOneNotice = {};

  switch (bk341FormType) {
    case '309A':
    case '309B': {
      threeFourOneNotice = {
        presumptionOfAbuse: noticeState.presumptionOfAbuse,
        deadlines: [noticeState.dischargeable],
        caseChapterFillingDate: noticeState.caseChapterFillingDate,
        caseChapter: noticeState.caseChapter,
        caseConvertedChapter: noticeState.caseConvertedChapter,
        caseConvertedDate: noticeState.caseConvertedDate,
        meetingInformation: {
          date: noticeState.meetingDate,
          time: noticeState.meetingTime,
          location: noticeState.meetingLocation,
        },
        debtors: noticeState.debtors,
        debtorAttorney: noticeState.debtorAttorney,
        debtorTrustee: noticeState.debtorTrustee,
        bankruptcyClerk: noticeState.bankruptcyClerk,
      };

      return threeFourOneNotice;
    }
    case '309G':
    case '309H':
    case '309I': {
      return {};
    }
    case '309D':
    case '309E1':
    case '309E2':
    case '309F1':
    case '309F2': {
      return {};
    }
    default:
      return {};
  }
};

const filterServedParties = (obj: any) => {
  const result: any = {};

  for (const key in obj) {
    const value = obj[key];

    if (typeof value === 'object' && value !== null) {
      const filteredNested = filterServedParties(value);
      if (Object.keys(filteredNested).length > 0) {
        result[key] = filteredNested;
      }
    } else if (value === true) {
      result[key] = value;
    }
  }

  return result;
};
const getEDCAData = (noticeState: formDataTypes) => {
  return {
    servedParties: filterServedParties(noticeState.edcaServedParty),
    aboutCase: {
      docketControlNumber: noticeState.docketControlNumber,
      chapter7case: {
        isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter7case.name),
        ruleOne: {
          isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter7case.ruleOne.name),
          ruleOne: {
            isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter7case.ruleOne.ruleOne.name),
          },
          ruleTwo: {
            isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter7case.ruleOne.ruleTwo.name),
            ecfNumber: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter7case.ruleOne.ruleTwo.name)
              ? noticeState.edcaChapter7ECFNumber
              : '',
          },
        },
      },
      chapter9case: {
        isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter9case.name),
        ruleOne: {
          isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter9case.ruleOne.name),
          ecfNumber: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter9case.ruleOne.name)
            ? noticeState.edcaChapter9ECFNumber
            : '',
        },
      },
      chapter15case: {
        isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter15case.name),
      },
      chapter12Or13case: {
        isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter12Or13case.name),
        ruleOne: {
          isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter12Or13case.ruleOne.name),
        },
        ruleTwo: {
          isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter12Or13case.ruleTwo.name),
        },
        ruleThree: {
          isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter12Or13case.ruleThree.name),
          ecfNumber: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter12Or13case.ruleThree.name)
            ? noticeState.edcaChapter12or13ECFNumber
            : '',
        },
      },
      chapter11case: {
        isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter11case.name),
        ruleOne: {
          isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter11case.ruleOne.name),
        },
        ruleTwo: {
          isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter11case.ruleTwo.name),
          ecfNumber: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.chapter11case.ruleTwo.name)
            ? noticeState.edcaChapter11ECFNumber
            : '',
        },
      },
      adversaryProceeding: {
        isChecked: noticeState.edcaCaseProceeding.includes(mapCaliforniaRules.adversaryProceeding.name),
      },
    },
  };
};

const getClientCategory = (clientCategory: string) => {
  //TODO: Find out the remaining organization types what do they map to
  // 'Panel Trustee',
  // 'Subchapter V Trustee',
  // 'Equity Receiver',
  // 'Probate & Estate Practitioner',
  // 'Other Legal Practitioner',

  const organizationToCategoryMapper: { [key: string]: string } = {
    USTP: 'USTP',
    'Bankruptcy Practitioner': 'Bankruptcy Practitioner',
    'Chapter 13 Trustee': '13 Trustees',
  };

  return organizationToCategoryMapper[clientCategory];
};

const getCaseNumbers = (noticeState: formDataTypes) => {
  return [noticeState.caseNumber.value, ...noticeState.jointCaseNumbers.map((caseNumber) => caseNumber.value)];
};

const getCaseCreditors = (noticeState: formDataTypes, preComputed?: any[]) => {
  const creditors = noticeState.creditors || [];

  const userUploadedAddresses = noticeState.userUploadedAddresses;
  const isCaliforniaEastern = noticeState.courtState === 'California' && noticeState.courtDistrict === 'Eastern';

  const items = preComputed ?? [
    ...(noticeState.caseSource.masterMailingMatrix ? creditors : []),
    ...(noticeState.caseSource.userSupplied ? userUploadedAddresses : []),
  ];

  // Filter out recipients with no address lines before sending to the backend
  const itemsWithAddress = items.filter((item) => Array.isArray(item.address) && item.address.length > 0);

  return itemsWithAddress.map((item) => {
    const itemWithIsExcluded = {
      ...item,
      isExcluded: isRecipientExcluded(item),
      ...(isCaliforniaEastern && (item.isFirstClass || item.isOvernight || item.isCertified)
        ? { californiaServiceDelivery: noticeState.californiaServiceDelivery[item.id] }
        : {}),
    };
    return Object.fromEntries(Object.entries(itemWithIsExcluded).filter(([_, v]) => v !== '')); //remove relationshipToParty if empty
  });
};

const getCaseParties = (noticeState: formDataTypes, preComputed?: any[]) => {
  const isCaliforniaEastern = noticeState.courtState === 'California' && noticeState.courtDistrict === 'Eastern';
  const caseParties = preComputed ?? (noticeState.caseParties || []);

  // Filter out recipients with no address lines before sending to the backend
  const partiesWithAddress = caseParties.filter((item: any) => Array.isArray(item.address) && item.address.length > 0);

  return partiesWithAddress.map((item: any) => {
    const itemWithIsExcluded = {
      ...item,
      name: item.name,
      isExcluded: isRecipientExcluded(item),
      ...(isCaliforniaEastern && (item.isFirstClass || item.isOvernight || item.isCertified)
        ? { californiaServiceDelivery: noticeState.californiaServiceDelivery[item.id] }
        : {}),
    };
    return Object.fromEntries(Object.entries(itemWithIsExcluded).filter(([_, v]) => v !== ''));
  });
};

export const getDebtorName = (noticeState: formDataTypes) => {
  if (noticeState.courtState === 'New Jersey') {
    if (noticeState.newJerseyAttestation.attestationType === 'attorney') {
      return noticeState.newJerseyAttestation.directClientName;
    } else if (noticeState.newJerseyAttestation.attestationType === 'secretary') {
      return noticeState.newJerseyAttestation.secondaryClientName;
    }
  }
  return noticeState.caseData?.debtorName;
};

export const buildBankruptcyNoticeData = (noticeState: formDataTypes, mappedRecipients?: [any[], any[]]) => {
  const [preComputedCaseParties, preComputedCreditors] = mappedRecipients ?? [undefined, undefined];
  const jobData = {
    type: 'bankruptcy notice',
    courtLocation: {
      state: noticeState.courtState,
      district: stateDistricts[noticeState.courtState as string].includes(noticeState.courtDistrict)
        ? noticeState.courtDistrict
        : '',
      division: noticeState.courtDivision,
    },
    ...(noticeState.adversaryProceeding.isChecked && { adversaryProceeding: noticeState.adversaryProceeding }),
    caseNumbers: getCaseNumbers(noticeState),
    rawCaseNumbers: noticeState.rawCaseNumbers,
    debtorName: getDebtorName(noticeState),
    ...(noticeState.caseData?.jointDebtorName && { jointDebtorName: noticeState.caseData.jointDebtorName }),
    ...(noticeState.jointCaseNumbers?.length > 0 && {
      jointCaseNumberDebtorNames: noticeState.jointCaseNumbers.map((jcn) => jcn.debtorName ?? ''),
    }),
    orderPreferences: {
      format: noticeState.orderPreferences.format.toLowerCase(),
      sides: noticeState.orderPreferences.sides.toLowerCase(),
      service: noticeState.orderPreferences.service.toLowerCase(),
    },
    returnAddress: noticeState.returnAddresses.find((item) => item.selected),
    ...(noticeState.includeCertificateOfService && {
      signature: noticeState.signatureBlocks.find((item) => item.selected),
    }),
    ...(noticeState.includeReturnEnvelope && { returnEnvelope: noticeState.returnEnvelope }),
    caseParties: noticeState.caseSource.importCaseParties || noticeState.certificatePreferences.shouldIncludeNEF ? getCaseParties(noticeState, preComputedCaseParties) : [],
    attorneys: noticeState.caseSource.masterMailingMatrix ? noticeState.attorneys : [],
    creditors: getCaseCreditors(noticeState, preComputedCreditors),
    certificatePreferences: noticeState.certificatePreferences,
    caseChapter: noticeState.caseChapter,
    judgeName: noticeState.judgeName,
    documents:
      noticeState.caseDocketEntries.length > 0
        ? noticeState.caseDocketEntries.filter((item) => item.isChecked)
        : generateDocuments(noticeState.uploadedPdfs),
    ...(noticeState.uploadedBallot.length !== 0 && { ballots: generateDocuments(noticeState.uploadedBallot) }),
    ...(noticeState.uploadedProofOfClaim.length !== 0 && {
      proofOfClaims: generateDocuments(noticeState.uploadedProofOfClaim),
    }),
    specialRules: {
      newJersey:
        noticeState.courtState === 'New Jersey'
          ? {
              attestation: {
                attestationType: noticeState.newJerseyAttestation.attestationType,
                clientName:
                  noticeState.newJerseyAttestation.attestationType === 'attorney'
                    ? noticeState.newJerseyAttestation.directClientName
                    : noticeState.newJerseyAttestation.secondaryClientName,
              },
            }
          : null,
      california:
        noticeState.courtState === 'California' && noticeState.courtDistrict === 'Eastern'
          ? getEDCAData(noticeState)
          : null,
    },
    clientCategory: getClientCategory(noticeState.clientCategory),
    paymentMethodId: noticeState.paymentMethodId,
  };

  let formData = new FormData();

  const jobBlob = JSON.stringify(jobData);

  formData.set('job', jobBlob);

  if (noticeState.uploadedPdfs) {
    const files = noticeState.uploadedPdfs.map((item) => convertToFile(item));

    files.forEach((fileData) => {
      formData.append('files', fileData); // Append each converted file
    });
  }

  if (noticeState.uploadedBallot) {
    const ballotFiles = noticeState.uploadedBallot.map((item) => convertToFile(item));
    ballotFiles.forEach((fileData) => {
      formData.append('ballotFiles', fileData);
    });
  }

  if (noticeState.uploadedProofOfClaim) {
    const proofOfClaims = noticeState.uploadedProofOfClaim.map((item) => convertToFile(item));
    proofOfClaims.forEach((fileData) => {
      formData.append('proofOfClaimFiles', fileData);
    });
  }

  return formData;
};

export const build341NoticeData = (noticeState: formDataTypes, bk341FormType: string) => {
  const jobData = {
    type: '341 notice',
    courtLocation: {
      state: noticeState.courtState,
      district: stateDistricts[noticeState.courtState as string].includes(noticeState.courtDistrict)
        ? noticeState.courtDistrict
        : '',
      division: noticeState.courtDivision,
    },
    caseNumber: noticeState.caseNumber,
    debtorName: noticeState.caseData?.debtorName,
    orderPreferences: {
      format: noticeState.orderPreferences.format.toLowerCase(),
      sides: noticeState.orderPreferences.sides.toLowerCase(),
      service: noticeState.orderPreferences.service.toLowerCase(),
    },
    returnAddress: noticeState.returnAddresses[0],
    signature: noticeState.signatureBlocks[0],
    caseParties: noticeState.caseParties,
    attorneys: noticeState.attorneys,
    creditors: noticeState.creditors,
    certificatePreferences: noticeState.certificatePreferences,
    caseChapter: noticeState.caseData?.case?.caseType?.caseTypeGroup,
    judgeName: noticeState.judgeName,
    documents:
      noticeState.caseDocketEntries.length > 0
        ? noticeState.caseDocketEntries.filter((item) => item.isChecked)
        : generateDocuments(noticeState.uploadedPdfs),
    specialRules: {
      newJersey: noticeState.newJerseyAttestation,
      california: getEDCAData(noticeState),
    },
    informationByJobType: {
      threeFourOneNotice: {
        ...generate341Data(bk341FormType, noticeState),
        formType: bk341FormType,
        caseChapter: noticeState.caseChapter,
        caseConvertedChapter: noticeState.caseConvertedChapter,
        caseChapterFillingDate: noticeState.caseChapterFillingDate,
        caseConvertedDate: noticeState.caseConvertedDate,
        dischargeable: noticeState.dischargeable,
        creditorsProofOfClaim: noticeState.creditorsProofOfClaim,
        governmentProofOfClaim: noticeState.governmentProofOfClaim,
      },
    },
    paymentMethodId: noticeState.paymentMethodId,
  };

  const jobBlob = JSON.stringify(jobData);

  let formData = new FormData();

  formData.set('job', jobBlob);

  if (noticeState.caseSource.userSupplied) {
    const files = noticeState.uploadedPdfs.map((item) => convertToFile(item));

    files.forEach((fileData) => {
      formData.append('files', fileData); // Append each converted file
    });
  }

  return formData;
};

const buildDirectMailingNoticeData = (noticeState: formDataTypes) => {
  const jobData = {
    type: 'direct mailing',
    dmMailingType: noticeState.directMailingType?.type,
    ...(noticeState.caseNumber && { caseNumbers: getCaseNumbers(noticeState) }),
    orderPreferences: {
      sides: noticeState.orderPreferences.sides.toLowerCase(),
      service: noticeState.orderPreferences.service.toLowerCase(),
      isBlankPageIncluded: noticeState.orderPreferences.blankPage !== '',
      ink: noticeState.orderPreferences.inkColor,
      barAdvertisingRuleDisclosure: noticeState.barRuleDisclosure,
    },
    ...(noticeState.directMailingType?.type === 'direct_mail_non_personalized' && {
      returnAddress: noticeState.returnAddresses.find((item) => item.selected),
    }),
    ...(noticeState.directMailingType?.type === 'direct_mail_non_personalized' && {
      creditors: getCaseCreditors(noticeState),
    }),
    ...(noticeState.includeCertificateOfService && {
      signature: noticeState.signatureBlocks.find((item) => item.selected),
    }),
    ...(noticeState.certificatePreferences ? { certificatePreferences: noticeState.certificatePreferences } : {}),
    documents:
      noticeState.directMailingType?.type === 'direct_mail_non_personalized'
        ? generateDocuments(noticeState.uploadedPdfs)
        : generateDmPersonalizedDocument(noticeState.uploadedPdfs, noticeState.personalizedNumberOfPages),
    clientCategory: getClientCategory(noticeState.clientCategory),
    paymentMethodId: noticeState.paymentMethodId,
  };

  const jobBlob = JSON.stringify(jobData);

  let formData = new FormData();

  formData.set('job', jobBlob);

  if (noticeState.caseSource.userSupplied) {
    const files = noticeState.uploadedPdfs.map((item) => convertToFile(item));

    files.forEach((fileData) => {
      formData.append('files', fileData); // Append each converted file
    });
  }

  return formData;
};

const getOrderPreferences = (noticeState: formDataTypes) => ({
  format: noticeState.orderPreferences.format.toLowerCase(),
  sides: noticeState.orderPreferences.sides.toLowerCase(),
  service: noticeState.orderPreferences.service.toLowerCase(),
});

const getDocuments = (noticeState: formDataTypes) =>
  noticeState.caseDocketEntries.length > 0
    ? noticeState.caseDocketEntries.filter((item) => item.isChecked)
    : generateDocuments(noticeState.uploadedPdfs);

const getCourtData = (noticeState: formDataTypes) => {
  if (noticeState.mailingType === 'Federal Court') {
    return {
      courtLocation: {
        state: noticeState.courtState,
        district: stateDistricts[noticeState.courtState as string].includes(noticeState.courtDistrict)
          ? noticeState.courtDistrict
          : '',
        division: noticeState.courtDivision,
      },
    };
  }
  if (['State Court', 'Probate Estate'].includes(noticeState.mailingType)) {
    return {
      courtJurisdiction: [
        noticeState.courtJurisdiction.county,
        noticeState.courtJurisdiction.state,
        noticeState.courtJurisdiction.division,
      ].filter((item) => item !== ''),
    };
  }
  return {};
};

const getLegalMailingType = (mailingType: string) => {
  const legalMailingTypes: { [key: string]: string } = {
    'Federal Court': 'federal_court',
    'State Court': 'state_court',
    'Probate Estate': 'probate_estate',
  };

  return legalMailingTypes[mailingType];
};

const buildLegalMailingNoticeData = (noticeState: formDataTypes) => {
  const jobData = {
    type: 'legal',
    legalMailingType: getLegalMailingType(noticeState.mailingType),
    ...getCourtData(noticeState),
    caseNumbers: getCaseNumbers(noticeState),
    adversaryProceeding:
      noticeState.mailingType === 'Probate Estate'
        ? { estatePartyName: noticeState.legalCaseFields.estatePartyName }
        : {
            adversaryPlaintiff: noticeState.legalCaseFields.plaintiff,
            adversaryDefendant: noticeState.legalCaseFields.defendant,
          },
    documents: getDocuments(noticeState),
    ...(noticeState.includeReturnEnvelope && { returnEnvelope: noticeState.returnEnvelope }),
    clientCategory: getClientCategory(noticeState.clientCategory),
    creditors: getCaseCreditors(noticeState),
    orderPreferences: getOrderPreferences(noticeState),
    returnAddress: noticeState.returnAddresses.find((item) => item.selected),
    ...(noticeState.includeCertificateOfService && {
      signature: noticeState.signatureBlocks.find((item) => item.selected),
    }),
    certificatePreferences: noticeState.certificatePreferences,
    paymentMethodId: noticeState.paymentMethodId,
  };

  const jobBlob = JSON.stringify(jobData);

  let formData = new FormData();

  formData.set('job', jobBlob);

  if (noticeState.caseSource.userSupplied) {
    const files = noticeState.uploadedPdfs.map((item) => convertToFile(item));

    files.forEach((fileData) => {
      formData.append('files', fileData); // Append each converted file
    });
  }

  return formData;
};

const generateDocuments = (docs: PDFFile[]) => {
  return docs.map((item, index) => ({
    isChecked: true,
    pageCount: item.pageCount,
    docketText: item.documentDescription,
    fillingDate: new Date(),
    id: item.id,
    docketDescription: item.documentDescription,
    docketReferenceNumber: item.documentECFDocketNumber,
    caseDocumentId: '',
    docketFileLink: '',
    storedFile: {
      key: '',
      link: '',
    },
    caseNumberMatch: item.isOCRValid,
    ...(item.paperColor && { paperColor: item.paperColor }),
  }));
};

const generateDmPersonalizedDocument = (docs: PDFFile[], pagesPerMailing: string) => {
  return docs.map((item, index) => ({
    isChecked: true,
    pageCount: item.pageCount,
    docketText: item.documentDescription,
    fillingDate: new Date(),
    id: item.id,
    docketDescription: item.documentDescription,
    docketReferenceNumber: item.documentECFDocketNumber,
    caseDocumentId: '',
    docketFileLink: '',
    storedFile: {
      key: '',
      link: '',
    },
    numberOfPagesPerMailing: +pagesPerMailing,
  }));
};

const convertToFile = (fileData: PDFFile) => {
  const base64String = fileData.baseBuffer.split(',')[1];

  const byteCharacters = atob(base64String);

  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  // Create a Blob from the Uint8Array
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  // Create and return a File object
  return new File([blob], fileData.baseName, { type: 'application/pdf' });
};

export const buildNoticeCreationMapper = (
  noticeState: formDataTypes,
  noticeType: NoticeType,
  bk341FormType?: string,
  mappedRecipients?: [any[], any[]],
) => {
  switch (noticeType.type) {
    case 'notice-bk':
      return buildBankruptcyNoticeData(noticeState, mappedRecipients);
    case '341':
      return build341NoticeData(noticeState, bk341FormType ?? '');
    case 'direct':
      return buildDirectMailingNoticeData(noticeState);
    case 'legal':
      return buildLegalMailingNoticeData(noticeState);
    default:
      return null;
  }
};

export const isRecipientExcluded = (item: CaseParty | CreditorsStateType) => {
  return !(item.isFirstClass || item.isOvernight || item.isCertified || item.isCmEcf);
};
